import React, { useState } from 'react'
import { MapPin, AlertTriangle, Info, TrendingUp } from 'lucide-react'
import type { GeographicConcentration } from '@/types/risk-assessment'

interface PortfolioRiskHeatMapProps {
  geographic_data: GeographicConcentration
  onLocationClick?: (location: string) => void
}

export function PortfolioRiskHeatMap({ geographic_data, onLocationClick }: PortfolioRiskHeatMapProps) {
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)

  // Production-safe debugging for props
  React.useEffect(() => {
    const componentDebug = {
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'unknown',
      propsReceived: {
        geographic_data_type: typeof geographic_data,
        has_geographic_data: !!geographic_data,
        locations_type: typeof geographic_data?.locations,
        locations_is_array: Array.isArray(geographic_data?.locations),
        locations_length: Array.isArray(geographic_data?.locations) ? geographic_data.locations.length : 0,
        concentration_score: geographic_data?.concentration_score,
        sample_location: Array.isArray(geographic_data?.locations) && geographic_data.locations.length > 0
          ? geographic_data.locations[0]
          : null
      }
    }

    console.log('🗺️ PortfolioRiskHeatMap component received props:', componentDebug)

    try {
      sessionStorage.setItem('riskAssessment_heatMapProps', JSON.stringify(componentDebug))
    } catch (e) {
      // Ignore storage errors
    }

    // Also add a global debug function for production inspection
    (window as any).__debugRiskHeatMap = () => {
      console.log('🔍 Current geographic_data:', geographic_data)
      console.log('🔍 Locations array:', geographic_data?.locations)
      console.log('🔍 Component debug info:', componentDebug)
      return {
        geographic_data,
        debug: componentDebug
      }
    }
  }, [geographic_data])

  const getRiskColor = (riskLevel: 'low' | 'medium' | 'high') => {
    switch (riskLevel) {
      case 'low': return 'bg-green-500'
      case 'medium': return 'bg-yellow-500'
      case 'high': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getRiskTextColor = (riskLevel: 'low' | 'medium' | 'high') => {
    switch (riskLevel) {
      case 'low': return 'text-green-700'
      case 'medium': return 'text-yellow-700'
      case 'high': return 'text-red-700'
      default: return 'text-gray-700'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  // Add safety check and fallback for production issues
  const sortedLocations = React.useMemo(() => {
    if (!Array.isArray(geographic_data?.locations)) {
      console.warn('⚠️ geographic_data.locations is not an array:', geographic_data?.locations)
      return []
    }

    if (geographic_data.locations.length === 0) {
      console.warn('⚠️ geographic_data.locations is empty but component received geographic_data')
      console.warn('📊 Geographic data object:', geographic_data)

      // Try to extract from sessionStorage if available (debugging aid)
      try {
        const debugData = sessionStorage.getItem('riskAssessment_finalGeoData')
        if (debugData) {
          const parsed = JSON.parse(debugData)
          console.log('📋 Debug data from normalization:', parsed)
        }
      } catch (e) {
        // Ignore
      }

      return []
    }

    // Validate each location object
    const validLocations = geographic_data.locations.filter(location => {
      const isValid = location &&
        typeof location.city === 'string' &&
        typeof location.state === 'string' &&
        typeof location.percentage === 'number' &&
        typeof location.property_count === 'number' &&
        typeof location.total_value === 'number'

      if (!isValid) {
        console.warn('⚠️ Invalid location object found:', location)
      }

      return isValid
    })

    if (validLocations.length !== geographic_data.locations.length) {
      console.warn(`⚠️ Filtered out ${geographic_data.locations.length - validLocations.length} invalid locations`)
    }

    return [...validLocations].sort((a, b) => b.percentage - a.percentage)
  }, [geographic_data])

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Geographic Risk Heat Map</h3>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>Concentration Score:</span>
            <span className={`font-bold ${
              geographic_data.concentration_score <= 30 ? 'text-green-600' :
              geographic_data.concentration_score <= 60 ? 'text-yellow-600' :
              'text-red-600'
            }`}>
              {geographic_data.concentration_score}/100
            </span>
          </div>
        </div>
      </div>

      {/* Risk Legend */}
      <div className="flex items-center justify-center space-x-6 mb-6 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span className="text-sm text-gray-600">Low Risk</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-yellow-500 rounded"></div>
          <span className="text-sm text-gray-600">Medium Risk</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded"></div>
          <span className="text-sm text-gray-600">High Risk</span>
        </div>
      </div>

      {/* Interactive Location Grid */}
      <div className="space-y-4">
        {sortedLocations.length > 0 ? (
          sortedLocations.map((location, index) => (
          <div
            key={`${location.city}-${location.state}-${location.zip_code}`}
            className={`
              border rounded-lg p-4 cursor-pointer transition-all duration-200
              ${selectedLocation === `${location.city}, ${location.state}`
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }
            `}
            onClick={() => {
              const locationKey = `${location.city}, ${location.state}`
              setSelectedLocation(selectedLocation === locationKey ? null : locationKey)
              onLocationClick?.(locationKey)
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <MapPin className={`w-5 h-5 ${getRiskTextColor(location.risk_level)}`} />
                  <div className={`
                    absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white
                    ${getRiskColor(location.risk_level)}
                  `} />
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {location.city}, {location.state}
                  </div>
                  <div className="text-sm text-gray-500">
                    ZIP: {location.zip_code}
                  </div>
                </div>
              </div>

              <div className="text-right">
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">
                      {location.percentage.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-500">
                      of portfolio
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {location.property_count} {location.property_count === 1 ? 'property' : 'properties'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatCurrency(location.total_value)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Concentration Bar */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">Concentration Level</span>
                <span className={`text-xs font-medium ${getRiskTextColor(location.risk_level)}`}>
                  {location.risk_level.toUpperCase()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getRiskColor(location.risk_level)}`}
                  style={{ width: `${location.percentage}%` }}
                />
              </div>
            </div>

            {/* Expanded Details */}
            {selectedLocation === `${location.city}, ${location.state}` && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Average Property Value:</span>
                    <div className="font-medium">
                      {formatCurrency(location.total_value / location.property_count)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Market Diversification:</span>
                    <div className="font-medium">
                      {location.risk_level === 'low' ? 'Well Diversified' :
                       location.risk_level === 'medium' ? 'Moderately Concentrated' :
                       'Highly Concentrated'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <MapPin className="w-12 h-12 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Geographic Data Available</h4>
            <p className="text-sm text-center max-w-md">
              Add properties to your portfolio to see geographic distribution and concentration risk analysis.
            </p>
            <div className="mt-4 text-xs text-gray-400">
              Geographic data helps identify concentration risk across different markets
            </div>
          </div>
        )}
      </div>

      {/* Risk Assessment Summary */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">
              Geographic Diversification Analysis
            </h4>
            <p className="text-sm text-blue-800">
              {geographic_data.diversification_recommendation}
            </p>

            {geographic_data.concentration_score > 60 && (
              <div className="mt-2 flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-orange-800">
                  High concentration risk detected - consider expanding to new markets
                </span>
              </div>
            )}

            {geographic_data.concentration_score > 80 && (
              <div className="mt-2 flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-800">
                  Recommended action: Target 15-20% maximum exposure per market
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Market Statistics */}
      {sortedLocations.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-lg font-bold text-gray-900">
              {geographic_data.locations.length}
            </div>
            <div className="text-sm text-gray-600">
              Markets
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-lg font-bold text-gray-900">
              {Math.max(...geographic_data.locations.map(l => l.percentage)).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">
              Max Concentration
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-lg font-bold text-gray-900">
              {(100 / geographic_data.locations.length).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">
              Ideal Per Market
            </div>
          </div>
        </div>
      )}
    </div>
  )
}